import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from './components/Sidebar';

const ImageUploadForm = () => {
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [images, setImages] = useState([]);
  const [captions, setCaptions] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/brands`);
        const data = await response.json();
        setBrands(data);
      } catch (error) {
        console.error('Error fetching brands:', error);
      }
    };

    fetchBrands();
  }, []);

  useEffect(() => {
    fetchUploadedImages();
  }, []);

  const fetchUploadedImages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/upload`);
      if (response.ok) {
        const data = await response.json();
        setUploadedImages(data);
      } else {
        console.error('Error fetching images');
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const handleBrandSelect = (event) => {
    setSelectedBrandId(event.target.value);
  };

  const handleImageChange = (event) => {
    previews.forEach((url) => URL.revokeObjectURL(url)); // release previous previews
    const selected = Array.from(event.target.files);
    setImages(selected);
    setCaptions(selected.map(() => '')); // one (optional) caption slot per file
    setPreviews(selected.map((file) => URL.createObjectURL(file)));
  };

  const resetSelection = () => {
    previews.forEach((url) => URL.revokeObjectURL(url));
    setImages([]);
    setCaptions([]);
    setPreviews([]);
  };

  const handleCaptionChange = (index, value) => {
    setCaptions((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleUpload = async () => {
    if (!selectedBrandId) {
      alert('Please select a portfolio');
      return;
    }

    if (images.length === 0) {
      alert('Please select at least one image to upload');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      images.forEach((image, index) => {
        formData.append('images', image); // 'images' matches the backend's expected field name
        formData.append('captions', captions[index] || ''); // parallel caption per image (may be empty)
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/upload/${selectedBrandId}`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        alert('Images uploaded successfully');
        resetSelection();
        fetchUploadedImages(); // Refresh the uploaded images
      } else {
        console.error('Error uploading images:', response.statusText);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePortfolio = async (portfolioId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/upload/${portfolioId}`, { 
        method: 'DELETE' 
      });

      if (response.ok) {
        setUploadedImages(uploadedImages.filter((image) => image.id !== portfolioId));
        alert('Image has been deleted successfully');
      } else {
        const errorData = await response.json();
        console.error('Error deleting Image:', errorData);
      }
    } catch (error) {
      console.error('Error deleting Image:', error);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50" style={{ fontFamily: "Muli" }}>
      <Sidebar />
      <div className="w-full">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white/80 backdrop-blur px-6 py-4">
          <p className="text-sm font-medium text-gray-400">Admin Dashboard</p>
          <span className="text-sm font-semibold text-gray-700">Welcome, Admin</span>
        </div>

        <div className="mx-auto max-w-5xl px-6 py-8">
          {/* Page heading */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Upload Images to Portfolio</h1>
            <p className="mt-1 text-sm text-gray-500">Add images to a portfolio and give each one an optional caption.</p>
          </div>

          {/* Upload card */}
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            {/* Portfolio select */}
            <label className="block text-sm font-semibold text-gray-700 mb-2">Portfolio</label>
            <select
              className="w-full sm:w-80 cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
              value={selectedBrandId}
              onChange={handleBrandSelect}
            >
              <option value="">Select a portfolio</option>
              {brands.map((brand) => (
                <option key={brand._id} value={brand._id}>
                  {brand.name}
                </option>
              ))}
            </select>

            {/* Dropzone-style file picker */}
            <label
              htmlFor="portfolio-files"
              className="mt-5 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center transition hover:border-gray-900 hover:bg-gray-100"
            >
              <svg className="mb-3 h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <span className="text-sm font-semibold text-gray-700">Click to choose images</span>
              <span className="mt-1 text-xs text-gray-400">You can select multiple files (up to 10)</span>
              <input
                id="portfolio-files"
                type="file"
                onChange={handleImageChange}
                className="hidden"
                multiple
                accept="image/*"
              />
            </label>

            {/* Selected files with per-image captions */}
            {images.length > 0 && (
              <div className="mt-6">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Selected images <span className="text-gray-400">({images.length})</span>
                  </h3>
                  <button
                    type="button"
                    onClick={resetSelection}
                    className="text-xs font-medium text-gray-400 hover:text-red-500"
                  >
                    Clear all
                  </button>
                </div>

                <div className="space-y-3">
                  {images.map((image, index) => (
                    <div key={index} className="flex items-center gap-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
                      <img
                        src={previews[index]}
                        alt={image.name}
                        className="h-16 w-16 flex-shrink-0 rounded-lg object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="mb-2 truncate text-xs font-medium text-gray-500">{image.name}</p>
                        <input
                          type="text"
                          placeholder="Caption (optional)"
                          value={captions[index] || ''}
                          onChange={(e) => handleCaptionChange(index, e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload button */}
            <button
              className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-gray-900 px-8 py-3.5 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              onClick={handleUpload}
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Uploading...
                </>
              ) : (
                'Upload Images'
              )}
            </button>
          </div>

          {/* Uploaded images table */}
          <div className="mt-10">
            <h2 className="mb-4 text-xl font-bold text-gray-900">Uploaded Images</h2>
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">S/N</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Portfolio</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Image</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Caption</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {uploadedImages.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-400">
                          No images uploaded yet.
                        </td>
                      </tr>
                    ) : (
                      uploadedImages.map((image, index) => (
                        <tr className="transition hover:bg-gray-50" key={index}>
                          <td className="px-6 py-4 text-sm text-gray-500">{index + 1}</td>
                          <td className="px-6 py-4 text-sm font-medium capitalize">
                            <Link to={`/admin/portfolio/${image.brand}`} className="text-blue-600 hover:underline">
                              {image.brandName}
                            </Link>
                          </td>
                          <td className="px-6 py-4">
                            <img src={image.image} alt="" className="h-14 w-14 rounded-lg object-cover ring-1 ring-gray-200" />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {image.caption || <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              className="rounded-lg bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-500 hover:text-white"
                              onClick={() => handleDeletePortfolio(image.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadForm;
